import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Text,
  Button,
  Paper,
  Flex,
  SimpleGrid,
  NumberInput,
  Divider,
  Loader,
  Center,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { notifications } from "@mantine/notifications";
import TitleComponent from "@/components/shared/TitleComponent";
import { successNotification } from "@/shared/utils/successNotification";
import { errorNotification } from "@/shared/utils/errorNotification";
import { IconArrowLeft } from "@tabler/icons-react";
import { useUserContext } from "@/context/UserContext";
import { getCurrencyForCountry, formatPrice } from "@/shared/utils/formatCurrency";

interface PaymentConfig {
  creditPriceInr: number;
  creditPriceUsd: number;
  verificationFeeCredits: number;
  engagementFeeCredits: number;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

const cleanupRazorpay = () => {
  const scripts = document.querySelectorAll("script");
  scripts.forEach((script) => {
    if (script.src.includes("razorpay.com")) {
      script.remove();
    }
  });
  const containers = document.querySelectorAll(".razorpay-container");
  containers.forEach((container) => container.remove());
  if (window.Razorpay) {
    (window as any).Razorpay = undefined;
  }
};

export default function BuyCreditsPage() {
  const navigate = useNavigate();
  const { userDetails } = useUserContext();
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [creditCount, setCreditCount] = useState<number | string>("");
  const [checkingOut, setCheckingOut] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const currency = getCurrencyForCountry(userDetails?.company?.headquartersCountry);

  useEffect(() => {
    return () => {
      cleanupRazorpay();
    };
  }, []);

  useEffect(() => {
    trpc.paymentConfig.get
      .query()
      .then((data) => setConfig(data as PaymentConfig))
      .catch(handleTrpcError)
      .finally(() => setLoadingConfig(false));
  }, []);

  const pricePerCredit = config ? (currency === "INR" ? config.creditPriceInr : config.creditPriceUsd) / 100 : 0;
  const verificationCredits = config?.verificationFeeCredits ?? 1;
  const collaborationCredits = config?.engagementFeeCredits ?? 14;
  const count = typeof creditCount === "number" ? creditCount : 0;
  const total = count * pricePerCredit;

  const handleCheckout = async () => {
    if (!count || count < 1) return;
    setCheckingOut(true);

    if (!window.Razorpay) {
      const isLoaded = await new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });

      if (!isLoaded) {
        errorNotification({
          title: "Error",
          message: "Failed to load payment gateway. Please check your connection.",
        });
        setCheckingOut(false);
        return;
      }
    }

    try {
      const order = await trpc.payment.createTopUpOrder.mutate({ credits: count });

      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: "GTsetu",
        description: `Purchase ${order.credits} Credits`,
        order_id: order.orderId,
        prefill: order.prefill,
        theme: { color: "#2563EB" },
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
          razorpay_invoice_id: string;
          razorpay_invoice_receipt: string;
          razorpay_invoice_status: string;
        }) => {
          // Payment completed on Razorpay's side — now verify server-side
          setVerifying(true);
          try {
            const result = await trpc.payment.verifyTopUpPayment.mutate({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_invoice_id: response.razorpay_invoice_id,
              razorpay_invoice_receipt: response.razorpay_invoice_receipt,
              razorpay_invoice_status: response.razorpay_invoice_status,
              razorpay_signature: response.razorpay_signature,
            });

            if (result.credited) {
              successNotification({
                title: "Payment successful",
                message: `${result.creditsAdded} credits have been added to your account.`,
              });
            } else {
              // Signature valid but webhook hasn't processed yet
              notifications.show({
                title: "Payment received",
                message: result.message || "Your credits are being processed and will appear shortly.",
                color: "blue",
                position: "top-right",
              });
            }
            navigate("/credits");
          } catch {
            notifications.show({
              title: "Verification issue",
              message:
                "Your payment was received but we couldn't confirm it immediately. Your credits will appear shortly.",
              color: "yellow",
              position: "top-right",
            });
            navigate("/credits");
          } finally {
            setVerifying(false);
            setCheckingOut(false);
            setTimeout(cleanupRazorpay, 500);
          }
        },
        modal: {
          ondismiss: () => {
            setCheckingOut(false);
            setTimeout(cleanupRazorpay, 500);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response: any) => {
        const errorDescription = response?.error?.description || "Your payment could not be processed.";
        console.error("[PAYMENT_FAILED]", response?.error);
        errorNotification({
          title: "Payment failed",
          message: `${errorDescription} Please try again.`,
        });
        setCheckingOut(false);
        setTimeout(cleanupRazorpay, 500);
      });
      rzp.open();
    } catch (err) {
      handleTrpcError(err);
      setCheckingOut(false);
    }
  };

  return (
    <Box mih="100vh">
      <TitleComponent
        title="Purchase credits"
        description="Purchase credits in order to verify company profile and for collaborations."
        leftSection={
          <Button
            variant="outline"
            color="gray"
            p={8}
            onClick={() => navigate("/credits")}
            styles={{
              root: {
                borderColor: "#E5E7EB",
                borderRadius: 8,
                minWidth: "auto",
                height: "auto",
              },
            }}
          >
            <IconArrowLeft size={18} color="#374151" />
          </Button>
        }
      />

      <Container size="fluid" px="lg">
        {loadingConfig ? (
          <Center py="xl" mih="40vh">
            <Loader size="lg" />
          </Center>
        ) : (
          <>
            {/* Info Cards */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
              <Paper p="md" radius="md" style={{ backgroundColor: "var(--mantine-color-primary-0)" }}>
                <Flex justify="space-between" align="flex-start">
                  <Text fz="sm" fw={500} c="slate.8">
                    Company Verification
                  </Text>
                  <Text fz="sm" fw={500} c="slate.8" style={{ whiteSpace: "nowrap" }}>
                    {verificationCredits} Credit
                  </Text>
                </Flex>
                <Flex justify="space-between" align="flex-end" mt={4}>
                  <Text fz="xs" c="slate.5">
                    {verificationCredits} Credit is required for Company Verification
                  </Text>
                  <Text fz="xs" c="slate.5" style={{ whiteSpace: "nowrap", marginLeft: 8 }}>
                    ({verificationCredits} Credit = {formatPrice(verificationCredits * pricePerCredit, currency)})
                  </Text>
                </Flex>
              </Paper>

              <Paper p="md" radius="md" style={{ backgroundColor: "var(--mantine-color-primary-0)" }}>
                <Flex justify="space-between" align="flex-start">
                  <Text fz="sm" fw={500} c="slate.8">
                    Collaboration
                  </Text>
                  <Text fz="sm" fw={500} c="slate.8" style={{ whiteSpace: "nowrap" }}>
                    {collaborationCredits} Credits
                  </Text>
                </Flex>
                <Flex justify="space-between" align="flex-end" mt={4}>
                  <Text fz="xs" c="slate.5">
                    {collaborationCredits} Credits required for Collaboration
                  </Text>
                  <Text fz="xs" c="slate.5" style={{ whiteSpace: "nowrap", marginLeft: 8 }}>
                    ({collaborationCredits} Credits = {formatPrice(collaborationCredits * pricePerCredit, currency)})
                  </Text>
                </Flex>
              </Paper>
            </SimpleGrid>

            {/* Input + Breakdown */}
            <Box maw={480}>
              <NumberInput
                label="Add credits to purchase?"
                placeholder="Enter number"
                min={1}
                max={10000}
                value={creditCount}
                onChange={setCreditCount}
                withAsterisk
                mb="lg"
                styles={{ input: { borderRadius: 8 } }}
              />

              <Flex justify="space-between" mb="xs">
                <Text fz="sm" c="slate.6">
                  1 credit × {formatPrice(pricePerCredit, currency)}
                </Text>
                <Text fz="sm" c="slate.6">
                  {formatPrice(pricePerCredit, currency)}
                </Text>
              </Flex>

              <Flex justify="space-between" mb="md">
                <Text fz="sm" c="slate.6">
                  {count} credit{count !== 1 ? "s" : ""}
                </Text>
                <Text fz="sm" c="slate.6">
                  {formatPrice(total, currency)}
                </Text>
              </Flex>

              <Divider mb="md" />

              <Flex justify="space-between" mb="xl">
                <Text fz="md" fw={500}>
                  Total
                </Text>
                <Text fz="md" fw={500}>
                  {formatPrice(total, currency)}
                </Text>
              </Flex>

              <Button
                fullWidth
                onClick={handleCheckout}
                loading={checkingOut || verifying}
                disabled={!count || count < 1}
                radius="md"
              >
                {verifying ? "Verifying payment..." : "Check out"}
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
}
